import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LanguageToggle } from "../../components/layout/LanguageToggle";
import { FaceoffDot } from "../../components/rink/FaceoffDot";
import { JerseyNumber } from "../../components/rink/JerseyNumber";
import { Button } from "../../components/ui/Button";
import { ErrorState } from "../../components/ui/ErrorState";
import { ApiError } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/en";
import { errorMessageKey } from "./actions";
import { playerApi, type PlayerProfile } from "./api";
import { PhotoPanel } from "./PhotoPanel";
import { SongPanel } from "./SongPanel";
import { mergeProfile, profileQueryKey, usePlayerProfile } from "./queries";

const LOCKING_CODES = new Set(["locked", "photo_locked", "song_locked"]);

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** Minimal header (wordmark and language) on the boards; the player page has no operator shell. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-svh">
      <div className="border-b-4 border-ink bg-board py-3 shadow-board">
        <div className="mx-auto flex max-w-md items-center justify-between gap-3 px-4 lg:max-w-6xl lg:px-6">
          <span className="font-display text-xl leading-none font-extrabold tracking-[0.01em] text-ink uppercase">
            Goalhorns
          </span>
          <LanguageToggle languages={["en", "sr", "ru"]} />
        </div>
      </div>
      <main className="mx-auto flex w-full max-w-md flex-col gap-6 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:max-w-6xl lg:pb-32 lg:grid lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-start lg:gap-8 lg:px-6 lg:pt-8">
        {children}
      </main>
    </div>
  );
}

/**
 * Photo capped at 38svh (centre focal point) so the song editor starts in the first phone viewport.
 * With a photo, the numeral and name share one row and the number label is dropped (the numeral says it).
 * Without one, the blue-wash block carries the giant numeral and the label sits under the name.
 */
function Identity({ token, profile }: { token: string; profile: PlayerProfile }) {
  const { t } = useI18n();
  const photoId = profile.photoMediaId;
  const hasPhoto = photoId !== null;
  const numberLabel =
    profile.number !== null ? t("roster.number", { number: profile.number }) : t("roster.noNumber");
  const numeral = (
    <span aria-hidden="true" className="block shrink-0">
      <JerseyNumber
        value={profile.number ?? "–"}
        size={hasPhoto ? "lg" : "xl"}
        className="text-blue!"
      />
    </span>
  );
  return (
    <header className="flex flex-col">
      {hasPhoto ? (
        <img
          src={playerApi.mediaUrl(token, photoId)}
          alt={t("player.photoAlt")}
          className="max-h-[38svh] aspect-[4/5] w-full object-cover object-center"
        />
      ) : (
        <div className="flex justify-center bg-blue-wash py-8">{numeral}</div>
      )}
      <div
        className={
          hasPhoto ? "flex min-w-0 items-center gap-3 px-4 pt-3" : "flex flex-col gap-2 px-4 pt-4"
        }
      >
        {hasPhoto ? numeral : null}
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl leading-[0.92] font-extrabold tracking-[0.01em] break-words uppercase sm:text-display-md">
            {profile.name}
          </h1>
          {hasPhoto ? null : <p className="text-note text-ink-3">{numberLabel}</p>}
        </div>
      </div>
    </header>
  );
}

/** The player's page at /p/:token. Shows only the player's own profile, photo and song. */
export function PlayerPage({ token }: { readonly token: string }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const query = usePlayerProfile(token);
  const [notice, setNotice] = useState<string | null>(null);
  const key = profileQueryKey(token);

  if (query.isPending) {
    return (
      <Shell>
        <p role="status" className="px-4 pt-8 text-operate text-ink-2">
          {t("app.loading")}
        </p>
      </Shell>
    );
  }
  if (query.isError) {
    if (isNotFound(query.error)) {
      return (
        <Shell>
          <section className="flex flex-col gap-4 px-4 pt-8">
            <h1 className="font-display text-4xl leading-[0.92] font-extrabold uppercase">
              {t("player.invalidTitle")}
            </h1>
            <ErrorState>{t("player.invalidFull")}</ErrorState>
          </section>
        </Shell>
      );
    }
    return (
      <Shell>
        <div className="px-4 pt-8">
          <ErrorState
            action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>}
          >
            {t(errorMessageKey(query.error))}
          </ErrorState>
        </div>
      </Shell>
    );
  }

  const profile: PlayerProfile = query.data;
  const applyProfile = (next: PlayerProfile) =>
    queryClient.setQueryData<PlayerProfile>(key, (prev) => mergeProfile(prev, next));
  const onFailure = (error: unknown) => {
    if (error instanceof ApiError && LOCKING_CODES.has(error.code)) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
  const onSuccess = (messageKey: MessageKey) => setNotice(t(messageKey));
  const panelProps = { token, profile, onProfile: applyProfile, onFailure, onSuccess };

  return (
    <Shell>
      <div className="flex flex-col gap-6 lg:sticky lg:top-6 lg:self-start">
        <Identity token={token} profile={profile} />
        {profile.locked ? (
          <div role="status" className="flex flex-col gap-1 bg-red px-4 py-3 text-white">
            <p className="font-display text-xl leading-none font-extrabold uppercase">
              {t("player.lockedTitle")}
            </p>
            <p>{t("player.lockedBanner")}</p>
            <p className="text-note">{t("player.lockedExplain")}</p>
          </div>
        ) : null}
      </div>
      <div className="flex flex-col gap-5 px-4 lg:px-0">
        {notice ? (
          <p
            role="status"
            className="flex items-center gap-3 rounded-control bg-blue-wash px-4 py-3 text-operate text-ink"
          >
            <FaceoffDot tone="blue" />
            {notice}
          </p>
        ) : null}
        <SongPanel {...panelProps} />
        <PhotoPanel {...panelProps} />
      </div>
    </Shell>
  );
}
